import { sendRequestViaTauri } from '../../utils/tauri';
import { clampOutput } from './outputBudget';
import { looksSecret, redactSecretsInText } from './secretHeuristics';

/**
 * `fetch_url` — read one public web page as text (22.08.2026).
 *
 * The gap it closes: `import_spec` can fetch a spec by URL, and `web_search`
 * can find the docs page, but the spec link is usually ON the docs page, not
 * in a search snippet (verified for Brevo: the only spec URL is buried in
 * developers.brevo.com). This lets the model open that page, read its links,
 * and hand the right one to import_spec — instead of asking the user for it.
 *
 * The request goes through the app's Rust HTTP client (same as import_spec),
 * from the user's machine, with no auth, no cookies and an explicit
 * `timeout_secs` — tauriAxiosAdapter drops timeouts, so apiService is never
 * the right transport for a model-chosen URL.
 *
 * What it refuses BEFORE any request (`isFetchableUrl`): anything that is not
 * public http(s). Loopback, private, link-local, CGNAT, multicast, the cloud
 * metadata range (169.254.169.254 is link-local), `.local`/`.internal`/
 * `.localhost`/`.home.arpa` names, userinfo in the URL, and query strings that
 * carry a credential-looking key or value. Residual, documented in the PLAN: a
 * public hostname that resolves to a private address (DNS rebinding) and a
 * redirect into a private range are not checkable from JS — the same exposure
 * the Request Builder already has.
 */

/** Characters of page text handed to the model (head + tail, see outputBudget). */
export const FETCH_URL_TEXT_BUDGET = 8000;
/** Bytes of response body we are willing to parse at all. */
export const FETCH_URL_MAX_BYTES = 2 * 1024 * 1024;
/** Seconds before the Rust client gives up — a docs page, not a model turn. */
export const FETCH_URL_TIMEOUT_SECS = 20;
/** Links returned, spec-like first. */
export const FETCH_URL_MAX_LINKS = 40;
/**
 * Calls per USER TURN (NB2, Reviewer 22.08.2026). "Follow at most 3 pages" was
 * prompt-only; with Auto-run on, a prompt-injected page could keep the model
 * fetching until the context filled. The panel's executeTool counts calls per
 * turn (reset on every user message) and answers the 4th with this error.
 */
export const FETCH_URL_PER_TURN_MAX = 3;
export const FETCH_URL_TURN_LIMIT_ERROR =
  'fetch_url limit reached for this turn — import what you have or ask the user';
/** Characters of a detected spec shown (the model must import it, not read it). */
const SPEC_PREVIEW_CHARS = 2000;

const LINK_TEXT_MAX = 80;
const LINK_HREF_MAX = 200;
const TITLE_MAX = 200;

/** Query keys whose VALUE is a credential by convention. */
const SECRET_QUERY_KEY = /(^|_)(token|key|secret|password|passwd|pwd|sig|signature|auth|session|code|apikey|api_key|access_token)$/i;

const BLOCKED_HOST_SUFFIXES = ['.localhost', '.local', '.internal', '.home.arpa'];

const SPEC_LINK = /openapi|swagger|postman|api-docs|\.ya?ml(\?|$)|\.json(\?|$)/i;

/** Parse a dotted-quad IPv4 (the URL parser already normalised shorthand and numeric forms). */
function parseIpv4(host) {
  const m = /^(\d{1,3})\.(\d{1,3})\.(\d{1,3})\.(\d{1,3})$/.exec(host);
  if (!m) return null;
  const octets = m.slice(1).map(Number);
  return octets.every((o) => o <= 255) ? octets : null;
}

/** Public-internet IPv4 only: everything reserved, private or link-local is false. */
function isPublicIpv4([a, b]) {
  if (a === 0) return false;                       // 0.0.0.0/8 "this network"
  if (a === 10) return false;                      // 10/8
  if (a === 100 && b >= 64 && b <= 127) return false; // 100.64/10 CGNAT
  if (a === 127) return false;                     // loopback
  if (a === 169 && b === 254) return false;        // link-local, incl. 169.254.169.254
  if (a === 172 && b >= 16 && b <= 31) return false; // 172.16/12
  if (a === 192 && b === 168) return false;        // 192.168/16
  if (a >= 224) return false;                      // multicast + reserved (224/4, 240/4)
  return true;
}

/** IPv6 literal (URL.hostname keeps the brackets). */
function isPublicIpv6(bracketed) {
  const host = bracketed.slice(1, -1).toLowerCase();
  if (host === '::1' || host === '::') return false;
  // IPv4-mapped (::ffff:10.0.0.1 or ::ffff:a00:1) — the URL parser renders
  // the dotted form; check both spellings.
  const mapped = /^::ffff:(\d{1,3}\.\d{1,3}\.\d{1,3}\.\d{1,3})$/.exec(host);
  if (mapped) {
    const v4 = parseIpv4(mapped[1]);
    return v4 ? isPublicIpv4(v4) : false;
  }
  if (/^::ffff:[0-9a-f]{1,4}:[0-9a-f]{1,4}$/.test(host)) return false;
  const first = parseInt(host.split(':')[0] || '0', 16);
  if ((first & 0xfe00) === 0xfc00) return false;   // fc00::/7 unique local
  if ((first & 0xffc0) === 0xfe80) return false;   // fe80::/10 link-local
  if ((first & 0xff00) === 0xff00) return false;   // ff00::/8 multicast
  return true;
}

/**
 * Decide, from the URL alone, whether fetch_url may request it.
 *
 * @returns {{ ok: true, url: string } | { ok: false, error: string }}
 */
export function isFetchableUrl(raw) {
  const text = String(raw ?? '').trim();
  if (!text) return { ok: false, error: 'url is required' };

  let parsed;
  try {
    parsed = new URL(text);
  } catch {
    return { ok: false, error: 'url must be an absolute http(s) URL' };
  }

  if (parsed.protocol !== 'http:' && parsed.protocol !== 'https:') {
    return { ok: false, error: `Only http(s) URLs can be fetched (got ${parsed.protocol.replace(':', '')})` };
  }
  if (parsed.username || parsed.password) {
    return { ok: false, error: 'URLs with embedded credentials are not fetched' };
  }

  // Normalise BEFORE matching (F1, Tester 22.08.2026): the WHATWG parser
  // lowercases and punycodes a hostname but KEEPS a trailing dot for names
  // (`localhost.` is the absolute-DNS spelling of localhost and resolves to
  // 127.0.0.1), so every suffix rule missed it. Strip trailing dots; IDNA is
  // already done by `new URL` (hostname is ASCII/punycode here).
  const host = parsed.hostname.toLowerCase().replace(/\.+$/, '');
  if (!host) return { ok: false, error: 'url has no host' };
  if (host === 'localhost' || BLOCKED_HOST_SUFFIXES.some((s) => host.endsWith(s))) {
    return { ok: false, error: 'Only public hosts can be fetched' };
  }
  if (host.startsWith('[')) {
    if (!isPublicIpv6(host)) return { ok: false, error: 'Only public addresses can be fetched' };
  } else {
    const v4 = parseIpv4(host);
    if (v4 && !isPublicIpv4(v4)) return { ok: false, error: 'Only public addresses can be fetched' };
  }

  for (const [key, value] of parsed.searchParams.entries()) {
    if (SECRET_QUERY_KEY.test(key) || looksSecret(key, value)) {
      return { ok: false, error: `The url's query string looks like it carries a credential (${key}) — not fetched` };
    }
  }

  return { ok: true, url: parsed.toString() };
}

/** Content-type header value (case-insensitive lookup), or ''. */
function contentTypeOf(headers) {
  if (!headers || typeof headers !== 'object') return '';
  const key = Object.keys(headers).find((k) => k.toLowerCase() === 'content-type');
  return key ? String(headers[key]) : '';
}

/** Does this look like something import_spec should handle, and which kind? */
export function detectSpec(text, contentType) {
  const head = String(text || '').slice(0, 4000);
  const trimmed = head.trimStart();
  const ct = String(contentType || '').toLowerCase();
  const structured = ct.includes('json') || ct.includes('yaml')
    || trimmed.startsWith('{') || /^(openapi|swagger):/i.test(trimmed);
  if (!structured) return null;
  if (/"openapi"\s*:|^openapi:|\nopenapi:/im.test(head)) return 'openapi';
  if (/"swagger"\s*:|^swagger:|\nswagger:/im.test(head)) return 'swagger';
  if (/_postman_id/.test(head)) return 'postman';
  return null;
}

const BLOCK_TAGS = new Set([
  'P', 'DIV', 'BR', 'LI', 'H1', 'H2', 'H3', 'H4', 'H5', 'H6', 'TR', 'TD', 'TH', 'SECTION', 'ARTICLE',
  'HEADER', 'FOOTER', 'PRE', 'BLOCKQUOTE', 'TABLE', 'UL', 'OL', 'DT', 'DD', 'NAV', 'ASIDE', 'MAIN', 'HR',
]);
const DROP_TAGS = new Set(['SCRIPT', 'STYLE', 'NOSCRIPT', 'TEMPLATE', 'SVG', 'IFRAME']);

/**
 * One pass over the tree, appending text and a newline around block elements.
 * A walk, not DOM mutation: inserting a text node per block element makes
 * jsdom (and WebKit) re-index siblings, which is quadratic on a long page.
 */
function collectText(node, out) {
  if (node.nodeType === 3) { // TEXT_NODE
    out.push(node.data);
    return;
  }
  if (node.nodeType !== 1 && node.nodeType !== 9 && node.nodeType !== 11) return;
  const tag = node.nodeType === 1 ? node.tagName : '';
  if (DROP_TAGS.has(tag)) return;
  const block = BLOCK_TAGS.has(tag);
  if (block) out.push('\n');
  for (let child = node.firstChild; child; child = child.nextSibling) collectText(child, out);
  if (block) out.push('\n');
}

/**
 * HTML → { title, text, links }. DOMParser exists in WKWebView, WebView2 and
 * jsdom; scripts never execute in a parsed document.
 */
export function htmlToText(html, baseUrl) {
  const doc = new DOMParser().parseFromString(String(html || ''), 'text/html');

  const title = (doc.querySelector('title')?.textContent || '').replace(/\s+/g, ' ').trim().slice(0, TITLE_MAX);

  const seen = new Set();
  const links = [];
  doc.querySelectorAll('a[href]').forEach((a) => {
    let href;
    try {
      href = new URL(a.getAttribute('href'), baseUrl).toString();
    } catch {
      return;
    }
    if (!/^https?:/i.test(href) || seen.has(href)) return;
    seen.add(href);
    links.push({
      text: (a.textContent || '').replace(/\s+/g, ' ').trim().slice(0, LINK_TEXT_MAX),
      href: href.slice(0, LINK_HREF_MAX),
      spec: SPEC_LINK.test(href),
    });
  });
  // Stable: spec-like links first, original order within each group.
  const ordered = [...links.filter((l) => l.spec), ...links.filter((l) => !l.spec)]
    .slice(0, FETCH_URL_MAX_LINKS)
    .map(({ text, href }) => ({ text, href }));

  const parts = [];
  collectText(doc.body || doc, parts);
  const text = parts.join('')
    .replace(/[ \t\r\f\v]+/g, ' ')
    .replace(/ ?\n ?/g, '\n')
    .replace(/\n{2,}/g, '\n')
    .trim();

  return { title, text, links: ordered, link_count: links.length };
}

/**
 * Fetch a public page and return it as bounded text. Never throws.
 *
 * @param {{ url: string }} input
 */
export async function fetchUrl({ url } = {}) {
  const check = isFetchableUrl(url);
  if (!check.ok) return { error: check.error };

  let res;
  try {
    res = await sendRequestViaTauri({
      method: 'GET',
      url: check.url,
      headers: {
        Accept: 'text/html,application/json,application/yaml,text/plain;q=0.9,*/*;q=0.5',
      },
      timeout_secs: FETCH_URL_TIMEOUT_SECS,
      // A model-chosen URL must never be fetched with the user's mTLS
      // identity or through their authenticated proxy (F4): utils/tauri.js
      // attaches both to every request unless told not to.
      attachClientCerts: false,
      attachProxy: false,
    });
  } catch (e) {
    return { error: redactSecretsInText(`Could not fetch the page: ${e?.message || String(e)}`) };
  }

  if (!res) return { error: 'Could not fetch the page: no response' };
  if (res.status >= 400) return { error: `Fetching the page returned HTTP ${res.status}` };
  if (res.temp_file) return { error: 'The page is too large to read (over 4 MB)' };
  if (res.data_base64 || (res.data && typeof res.data !== 'string' && typeof res.data !== 'object')) {
    return { error: 'The page is not a text page' };
  }

  const contentType = contentTypeOf(res.headers);
  const raw = typeof res.data === 'string' ? res.data : JSON.stringify(res.data ?? '');
  if (raw.length > FETCH_URL_MAX_BYTES) {
    return { error: `The page is too large to read (${Math.round(raw.length / 1024 / 1024)} MB)` };
  }

  const specHint = detectSpec(raw, contentType);
  if (specHint) {
    return {
      url: check.url,
      status: res.status,
      content_type: contentType,
      title: '',
      looks_like_api_spec: true,
      spec_hint: specHint,
      text: redactSecretsInText(raw.slice(0, SPEC_PREVIEW_CHARS)),
      truncated: raw.length > SPEC_PREVIEW_CHARS,
      total_chars: raw.length,
      links: [],
      link_count: 0,
      note: 'This is an API spec. Call import_spec with this url — do not paste the content.',
    };
  }

  const isHtml = contentType.toLowerCase().includes('html') || /^\s*<(!doctype|html|head|body)/i.test(raw);
  let page;
  try {
    page = isHtml
      ? htmlToText(raw, check.url)
      : { title: '', text: raw.replace(/\r/g, '').trim(), links: [], link_count: 0 };
  } catch (e) {
    return { error: `Could not read the page: ${e?.message || String(e)}` };
  }

  const clamped = clampOutput(page.text, FETCH_URL_TEXT_BUDGET);
  return {
    url: check.url,
    status: res.status,
    content_type: contentType,
    title: redactSecretsInText(page.title),
    text: redactSecretsInText(clamped),
    truncated: clamped !== page.text,
    total_chars: page.text.length,
    links: page.links.map((l) => ({ text: redactSecretsInText(l.text), href: l.href })),
    link_count: page.link_count,
  };
}

/**
 * What crosses back to the model. `text` is already clamped and scrubbed at
 * the source; this case exists so the default projector does not drop it.
 */
export function condenseFetchUrlResult(result) {
  if (!result || typeof result !== 'object') return result;
  return {
    url: result.url,
    status: result.status,
    content_type: result.content_type,
    title: result.title,
    text: clampOutput(result.text, FETCH_URL_TEXT_BUDGET),
    truncated: result.truncated,
    total_chars: result.total_chars,
    links: (result.links || []).slice(0, FETCH_URL_MAX_LINKS).map((l) => ({ text: l.text, href: l.href })),
    link_count: result.link_count,
    looks_like_api_spec: result.looks_like_api_spec,
    spec_hint: result.spec_hint,
    note: result.note,
  };
}
